import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { SidebarService } from './sidebar.util.service';
import { NavigationEnd, Router } from '@angular/router';
import { NbMenuService } from '@nebular/theme';
import { Subject, takeUntil } from 'rxjs';
import { ContextUpdatesService } from 'src/app/services/context-updates.service';
import { triggerAnalyticsEvent } from 'src/app/utils/analytics/trigger-analytics';
import { LastPageService } from 'src/app/services/last-visited-page/last-visited-page.service';
import { isEqual } from 'lodash';

@Component({
  selector: 'app-sidebar-v2',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
// implements OnChanges
export class SidebarV2Component implements OnDestroy {
  private destroy$ = new Subject<void>(); // Used for fetching the active menu item from the nb-menu component (tag: 'mainMenu' here)

  sidebarInitialized: boolean = true; // Used to hold the rendering of the sidebar till the context is ready

  currentClusterURL: string; // Current cluster URL from the URL (stack/stackName/cluster/clusterID)

  currentSidebarConfig: any; // Entire sidebar object which gets rendered

  @Output() byMainMenuActiveItemChange: EventEmitter<any> =
    new EventEmitter<any>();

  routerSubscriber;

  constructor(
    private sidebarUtilService: SidebarService,
    private menuService: NbMenuService,
    private router: Router,
    private contextUpdates: ContextUpdatesService,
    private lastPageService: LastPageService,
  ) {
    this.getSideBarConfig();

    this.contextUpdates.successfulContextUpdates.subscribe((context) => {
      this.getSideBarConfig().then(() => {
        const accountMenuItem = document.getElementsByClassName(
          'main-nb-user-component',
        )[0];
        if (this.router?.url?.includes('/accountSettings/')) {
          accountMenuItem?.classList?.add('active-account-settings');
        } else {
          accountMenuItem?.classList?.remove('active-account-settings');
        }
      });
    });

    this.routerSubscriber = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const menuItem = this.getMenuItemFromUrl(event.urlAfterRedirects);
        if (menuItem === 'STACK') {
          this.lastPageService.setLastVisitedPage(
            menuItem,
            event.urlAfterRedirects,
          );
          this.getSideBarConfig();
        }
      }
    });
  }

  patchCustomIcons() {
    setTimeout(() => {
      const customItems = document.querySelectorAll('.menu-item > a');
      customItems.forEach((itemEl: any) => {
        const title = itemEl?.textContent;
        const configItem = this.currentSidebarConfig?.find(
          (i) => i.title === title,
        );
        if (configItem?.iconComponent) {
          itemEl?.classList?.add('web-component-menu-item');

          if (itemEl?.children) {
            itemEl.children[0].style.display = 'none';
          }
          const img = document.createElement('img');
          img.src = configItem?.iconComponent;
          img.className = 'custom-icon';
          img.onclick = () => {
            this.router.navigate([configItem.link]);
          };
          img['nbTooltip'] = configItem?.title;
          itemEl?.prepend(img);
        }
      });
    });
  }

  private getSideBarConfig(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.sidebarUtilService
        .getSidebarConfig(this.router.url)
        .subscribe((sidebarConfig) => {
          const transformedSidebarConfig = sidebarConfig.map((item) => {
            const isImageIcon =
              typeof item.icon === 'string' && item.icon.startsWith('http');
            if (isImageIcon) {
              return {
                ...item,
                iconComponent: item.icon,
                icon: undefined,
              };
            }
            return item;
          });

          if (!isEqual(this.currentSidebarConfig, transformedSidebarConfig)) {
            // this.logSidebarDiff(this.currentSidebarConfig, transformedSidebarConfig);
            this.currentSidebarConfig = transformedSidebarConfig;
            this.patchCustomIcons();
          } else {
          }

          resolve(true);
        });
    });
  }

  // private logSidebarDiff(oldConfig: any[], newConfig: any[]) {
  //   const changes = [];

  //   const maxLength = Math.max(oldConfig?.length || 0, newConfig?.length || 0);

  //   for (let i = 0; i < maxLength; i++) {
  //     const oldItem = oldConfig?.[i];
  //     const newItem = newConfig?.[i];

  //     if (!oldItem) {
  //       changes.push({ index: i, type: 'added', newItem });
  //     } else if (!newItem) {
  //       changes.push({ index: i, type: 'removed', oldItem });
  //     } else if (!isEqual(oldItem, newItem)) {
  //       const fieldChanges = {};
  //       for (const key of new Set([
  //         ...Object.keys(oldItem),
  //         ...Object.keys(newItem),
  //       ])) {
  //         if (!isEqual(oldItem[key], newItem[key])) {
  //           fieldChanges[key] = {
  //             from: oldItem[key],
  //             to: newItem[key],
  //           };
  //         }
  //       }
  //       changes.push({ index: i, type: 'modified', changes: fieldChanges });
  //     }
  //   }

  //   if (changes.length > 0) {
  //     console.log('Sidebar config', changes);
  //   } else {
  //     console.log(
  //       'Sidebar config difference calculation found no actual diffs.',
  //     );
  //   }
  // }

  private getMenuItemFromUrl(url: string): string | null {
    const currentURL = this.router.url;
    const urlParams = currentURL?.split('/');
    if (
      ['stack', 'home']?.includes(urlParams?.[1]) ||
      urlParams?.[2] === 'cluster'
    ) {
      return 'STACK';
    }
    return;
  }

  nameComparator = (a, b) => {
    const nameA = a.name.toLowerCase();
    const nameB = b.name.toLowerCase();
    if (nameA < nameB) {
      return -1;
    } else if (nameA > nameB) {
      return 1;
    } else {
      return 0;
    }
  };

  // Helper functions
  // On click event from the mainMenu nb-menu
  // Used in disabling the active menu item from the other nb-menu (tag: 'footerMenu') in app-layout
  updateFooterMenuWithState() {
    // Removing all the active menu items by passing some random string to removeAllOtherActiveTabs
    this.removeAllOtherActiveTabs('_____remove-all-menu-items____');
    setTimeout(() => {
      this.menuService
        .getSelectedItem('mainMenu')
        .pipe(takeUntil(this.destroy$))
        .subscribe((menuItem) => {
          this.byMainMenuActiveItemChange.emit();
          if (
            menuItem?.item ||
            this.router.url.includes(this.currentClusterURL)
          ) {
            this.removeAllOtherActiveTabs(menuItem?.item?.title);
            triggerAnalyticsEvent(
              `LEFT_NAV_${menuItem?.item?.title?.toUpperCase()}`,
            );
          } else if (this.router.url.includes('settings')) {
            // patch: re-mark settings as active if environment was clicked on
            this.markSettingsActive();
          }
        });
    }, 1000);
  }

  // DOM manipulation because nb-menu doesn't have APIs to update the state for active menu items
  // Goes through the anchor tabs of the other menu and updates their classList
  removeAllOtherActiveTabs(currentlyActive: string) {
    const allMenus: any = document.querySelectorAll('nb-menu');
    const allAnchorTags = [...allMenus[1]?.getElementsByTagName('a')];
    for (let i = 0; i < allAnchorTags?.length; i++) {
      const item = allAnchorTags[i];
      item?.classList.remove('active');
    }
  }

  removeActiveStateFromSidebarConfig() {
    // Remove active state from current sidebarConfig too
    this.currentSidebarConfig.forEach((menuItem) => {
      menuItem.selected = false;
    });
  }

  markSettingsActive() {
    const allMenus: any = document.querySelectorAll('nb-menu');
    if (allMenus?.length) {
      const allAnchorTags = [...allMenus?.[1]?.getElementsByTagName('a')];
      allAnchorTags?.[1]?.classList?.add('active');
    }
  }

  setActiveState() {
    let urlParams;
    const currentURL = this.router.url;
    if (currentURL?.length > 0 && currentURL.indexOf('/') >= 0) {
      urlParams = currentURL.split('/');
      if (
        urlParams?.length > 1 &&
        (urlParams[1] === 'home' || urlParams[1] === 'stack') &&
        !(urlParams[3] === 'artifacts') &&
        !(urlParams[3] === 'dashboards')
      ) {
        this.currentSidebarConfig.forEach((item) => {
          if (item.title === 'Blueprint') {
            item.selected = true;
          }
        });
      } else if (urlParams?.length > 1 && urlParams[3] === 'artifacts') {
        this.currentSidebarConfig.forEach((item) => {
          if (item.title === 'Artifacts') {
            item.selected = true;
          }
        });
      } else if (urlParams?.length > 1 && urlParams[1] === 'settings') {
        this.currentSidebarConfig.forEach((item) => {
          if (item.title === 'Settings') {
            item.selected = true;
          }
        });
      } else if (urlParams?.length > 2 && urlParams[2] === 'cluster') {
        this.currentSidebarConfig.forEach((item) => {
          if (item.title === 'Environment') {
            item.selected = true;
          }
        });
      }
    }
  }

  ngOnDestroy() {
    this.lastPageService.resetData();
    this.routerSubscriber?.unsubscribe();
  }
}
